import React, { useState } from 'react';
import { ArrowLeft, MapPin, Mail, Loader2, CheckCircle } from 'lucide-react';
import { api } from '../lib/db';
import { useToast } from '../contexts/ToastContext';

export type StaticView = 'ABOUT' | 'HOW_IT_WORKS' | 'PRIVACY' | 'CONTACT' | 'TERMS' | 'RETURN_POLICY' | 'DISTANCE_SALES';

interface StaticPagesProps {
  view: StaticView;
  onBack: () => void;
}

const StaticPages: React.FC<StaticPagesProps> = ({ view, onBack }) => {
  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '' });
  const [isSending, setIsSending] = useState(false);
  const [isSent, setIsSent] = useState(false);
  const { addToast } = useToast();

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!contactForm.name || !contactForm.email || !contactForm.message) {
      addToast('Lütfen tüm alanları doldurunuz.', 'warning');
      return;
    }

    setIsSending(true);
    try {
        await api.contact.send(contactForm);
        setIsSent(true);
        addToast('Mesajınız yönetici paneline başarıyla iletildi.', 'success');
        setContactForm({ name: '', email: '', message: '' });
        
        // 5 saniye sonra başarı ekranını kaldır
        setTimeout(() => setIsSent(false), 5000);
    } catch (error: any) {
        console.error("İletişim formu hatası:", error);
        const errorMsg = error.message || 'Mesaj gönderilemedi. Lütfen daha sonra tekrar deneyin.';
        addToast(errorMsg, 'error');
    } finally {
        setIsSending(false);
    }
  };

  const renderContent = () => {
    switch (view) {
      case 'ABOUT':
        return (
          <div className="space-y-6">
            <h2 className="text-3xl font-black text-gray-900 dark:text-white">Hakkımızda</h2>
            <div className="prose dark:prose-invert max-w-none text-gray-600 dark:text-gray-300">
              <p>
                <strong>Cebime Gelsin</strong>, Türkiye'nin dijitalleşen pazar yeri ihtiyacına modern, hızlı ve güvenilir bir çözüm sunmak amacıyla kurulmuş yeni nesil bir platformdur.
              </p>
              <p>
                Emlak ve otomotiv sektöründeki karmaşık arama süreçlerini tersine çeviriyoruz. Müşterilerimizin dükkan dükkan gezerek veya binlerce ilan arasında kaybolarak zaman kaybetmesini engelliyor; ihtiyaçlarını sisteme girmeleri durumunda bölgedeki en iyi kurumsal firmaların onlara teklif sunmasını sağlıyoruz.
              </p>
              <h3 className="text-xl font-bold mt-6 mb-2 text-gray-800 dark:text-white">Vizyonumuz</h3>
              <p>
                Teknolojinin gücünü kullanarak alıcı ve satıcıyı en verimli şekilde buluşturan, güvene dayalı ve şeffaf bir ticaret ekosistemi oluşturmak.
              </p>
              <h3 className="text-xl font-bold mt-6 mb-2 text-gray-800 dark:text-white">Neden Biz?</h3>
              <ul className="list-disc pl-5 space-y-2">
                <li><strong>Sadece Onaylı Firmalar:</strong> Platformumuzdaki tüm hizmet sağlayıcılar VKN ve Yetki Belgesi kontrolünden geçer.</li>
                <li><strong>Tersine Teklif Sistemi:</strong> İlan aramak yok, talep oluşturmak var.</li>
                <li><strong>KVKK Uyumlu:</strong> Verileriniz sadece sizin seçtiğiniz firmalarla paylaşılır.</li>
              </ul>
            </div>
          </div>
        );

      case 'HOW_IT_WORKS':
        return (
          <div className="space-y-6">
            <h2 className="text-3xl font-black text-gray-900 dark:text-white">Nasıl Çalışır?</h2>
            <div className="grid gap-8 mt-8">
              <div className="flex gap-4">
                <div className="w-12 h-12 rounded-full bg-teal-100 dark:bg-teal-900 flex items-center justify-center text-teal-600 font-bold text-xl shrink-0">1</div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">Talebini Oluştur</h3>
                  <p className="text-gray-600 dark:text-gray-300 mt-1">Emlak veya araç arayışını detaylı filtre seçenekleriyle sisteme gir. Bütçeni ve kriterlerini belirt.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center text-blue-600 font-bold text-xl shrink-0">2</div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">Firmalara Ulaşsın</h3>
                  <p className="text-gray-600 dark:text-gray-300 mt-1">Talebin, bölgedeki ve ilgili kategorideki onaylı kurumsal üyelere (Emlak Ofisleri & Galeriler) anında iletilir.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-12 h-12 rounded-full bg-indigo-100 dark:bg-indigo-900 flex items-center justify-center text-indigo-600 font-bold text-xl shrink-0">3</div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">Teklifleri Karşılaştır</h3>
                  <p className="text-gray-600 dark:text-gray-300 mt-1">Firmalar ellerindeki portföyden sana en uygun olanları dijital teklif kartı olarak gönderir. Fiyatları ve yorumları incele.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-12 h-12 rounded-full bg-purple-100 dark:bg-purple-900 flex items-center justify-center text-purple-600 font-bold text-xl shrink-0">4</div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">İletişime Geç</h3>
                  <p className="text-gray-600 dark:text-gray-300 mt-1">Beğendiğin teklifi onayla, firma ile mesajlaş veya doğrudan iletişime geçerek işini hallet.</p>
                </div>
              </div>
            </div>
          </div>
        );

      case 'CONTACT':
        return (
          <div className="space-y-8">
            <h2 className="text-3xl font-black text-gray-900 dark:text-white">İletişim</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-6">
                <p className="text-gray-600 dark:text-gray-300">
                  Sorularınız, önerileriniz veya kurumsal iş birlikleri için bize aşağıdaki kanallardan ulaşabilirsiniz.
                </p>
                
                <div className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-xl">
                  <div className="w-10 h-10 bg-indigo-100 dark:bg-indigo-900 flex items-center justify-center text-indigo-600 rounded-full">
                    <MapPin className="w-5 h-5"/>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 dark:text-white">Adres</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Teknopark İstanbul, Pendik / İstanbul / Türkiye</p>
                  </div>
                </div>

                <div className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-xl">
                  <div className="w-10 h-10 bg-indigo-100 dark:bg-indigo-900 flex items-center justify-center text-indigo-600 rounded-full">
                    <Mail className="w-5 h-5"/>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 dark:text-white">E-posta</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">info@cebimegelsin.com</p>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 relative overflow-hidden">
                {isSent ? (
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-white dark:bg-gray-800 z-10 animate-in fade-in zoom-in">
                        <CheckCircle className="w-16 h-16 text-green-500 mb-4"/>
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white">Mesajınız Alındı!</h3>
                        <p className="text-gray-500 mt-2">En kısa sürede size dönüş yapacağız.</p>
                    </div>
                ) : (
                    <>
                    <h3 className="font-bold text-lg mb-4 text-gray-900 dark:text-white">Bize Ulaşın</h3>
                    <form className="space-y-4" onSubmit={handleContactSubmit}>
                    <div>
                        <label className="block text-xs font-bold text-gray-500 mb-1">AD SOYAD</label>
                        <input 
                        required
                        className="w-full p-3 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500 dark:text-white" 
                        placeholder="Adınız"
                        value={contactForm.name}
                        onChange={e => setContactForm({...contactForm, name: e.target.value})}
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-gray-500 mb-1">E-POSTA</label>
                        <input 
                        required
                        type="email"
                        className="w-full p-3 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500 dark:text-white" 
                        placeholder="ornek@email.com"
                        value={contactForm.email}
                        onChange={e => setContactForm({...contactForm, email: e.target.value})}
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-gray-500 mb-1">MESAJINIZ</label>
                        <textarea 
                        required
                        rows={4} 
                        className="w-full p-3 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500 dark:text-white" 
                        placeholder="Mesajınız..." 
                        value={contactForm.message}
                        onChange={e => setContactForm({...contactForm, message: e.target.value})}
                        />
                    </div>
                    <button 
                        type="submit" 
                        disabled={isSending}
                        className="w-full py-3 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 transition-colors flex items-center justify-center"
                    >
                        {isSending ? <><Loader2 className="w-5 h-5 animate-spin mr-2"/> Gönderiliyor...</> : 'Gönder'}
                    </button>
                    </form>
                    </>
                )}
              </div>
            </div>
          </div>
        );

      case 'PRIVACY':
      case 'TERMS':
      case 'RETURN_POLICY':
      case 'DISTANCE_SALES':
        return (
          <div className="space-y-6">
            <h2 className="text-3xl font-black text-gray-900 dark:text-white flex items-center">
              {view === 'PRIVACY' ? 'Gizlilik Politikası' : 
               view === 'RETURN_POLICY' ? 'Teslimat ve İade Şartları' :
               view === 'DISTANCE_SALES' ? 'Mesafeli Satış Sözleşmesi' :
               'Kullanıcı Sözleşmesi'}
            </h2>
            <div className="prose dark:prose-invert max-w-none text-gray-600 dark:text-gray-300 text-sm leading-relaxed whitespace-pre-wrap">
              {view === 'PRIVACY' ? `
Cebime Gelsin Gizlilik Politikası

1. Veri Toplama
Hizmetlerimizi kullanırken adınız, iletişim bilgileriniz, talep detaylarınız ve ödeme güvenliği için gerekli (ancak saklanmayan) bilgiler toplanmaktadır.

2. Veri Kullanımı
Toplanan veriler, sadece talep ettiğiniz hizmeti (emlak/araç teklifi) sağlamak ve güvenli ödeme işlemlerini gerçekleştirmek amacıyla kullanılır. Verileriniz, izniniz olmadan üçüncü taraflarla paylaşılmaz.

3. Çerezler (Cookies)
Sitemizde kullanıcı deneyimini iyileştirmek, site trafiğini analiz etmek ve reklam/pazarlama faaliyetleri yürütmek amacıyla çerezler kullanılmaktadır.

4. Güvenlik
Verileriniz 256-bit SSL sertifikası ile şifrelenerek korunmaktadır. Ödeme bilgileri Iyzico altyapısı üzerinden işlenir ve sunucularımızda saklanmaz.
              ` : view === 'RETURN_POLICY' ? `
Teslimat ve İade Şartları

1. Hizmetin Niteliği
Cebime Gelsin üzerinden satın alınan hizmetler (Hizmet Paketleri, Dopingler vb.), elektronik ortamda anında ifa edilen gayri maddi mallar kapsamındadır.

2. Teslimat
Satın alma işleminin tamamlanmasının ardından, ilgili hizmet hakkı (kredi, doping vb.) kullanıcının hesabına anında tanımlanır. Fiziksel bir kargo gönderimi yapılmamaktadır.

3. Cayma Hakkı İstisnaları
6502 sayılı Tüketicinin Korunması Hakkında Kanun ve Mesafeli Sözleşmeler Yönetmeliği uyarınca; "Elektronik ortamda anında ifa edilen hizmetler veya tüketiciye anında teslim edilen gayri maddi mallara ilişkin sözleşmeler" cayma hakkının istisnaları arasındadır. Bu nedenle, kullanılmış veya hesaba tanımlanmış hizmetlerin iadesi kural olarak mümkün değildir.

4. İade Prosedürü
Sistemden kaynaklı teknik bir hata nedeniyle hizmetin kullanılamaması durumunda, müşteri hizmetlerimizle iletişime geçilerek inceleme başlatılabilir. Hata tespit edilirse, ücret iadesi ödemenin yapıldığı karta 3-7 iş günü içinde yansıtılır.
              ` : view === 'DISTANCE_SALES' ? `
Mesafeli Satış Sözleşmesi

MADDE 1 - TARAFLAR
1.1. SATICI
Unvan: Cebime Gelsin Teknoloji A.Ş.
Adres: Teknopark İstanbul, Pendik / İstanbul
E-posta: info@cebimegelsin.com

1.2. ALICI
Sisteme üye olan ve hizmet satın alan kullanıcıdır.

MADDE 2 - KONU
İşbu sözleşmenin konusu, ALICI'nın SATICI'ya ait www.cebimegelsin.com internet sitesinden elektronik ortamda siparişini yaptığı aşağıda nitelikleri ve satış fiyatı belirtilen hizmetin satışı ve ifası ile ilgili olarak 6502 sayılı Tüketicinin Korunması Hakkında Kanun ve Mesafeli Sözleşmeler Yönetmeliği hükümleri gereğince tarafların hak ve yükümlülüklerinin saptanmasıdır.

MADDE 3 - SÖZLEŞME KONUSU HİZMET
Hizmetin Türü: Dijital İlan/Teklif Hakkı Paketi
Hizmet Bedeli: Ödeme sayfasında belirtilen tutardır.

MADDE 4 - GENEL HÜKÜMLER
4.1. ALICI, internet sitesinde sözleşme konusu hizmetin temel nitelikleri, satış fiyatı ve ödeme şekli ile teslimata ilişkin ön bilgileri okuyup bilgi sahibi olduğunu ve elektronik ortamda gerekli teyidi verdiğini beyan eder.
4.2. Hizmet, ödemenin onaylanmasını takiben derhal ALICI'nın hesabına tanımlanır.

MADDE 5 - CAYMA HAKKI
İşbu sözleşme konusu hizmet, elektronik ortamda anında ifa edilen bir hizmet olduğundan, Mesafeli Sözleşmeler Yönetmeliği'nin 15. maddesi gereğince ALICI'nın cayma hakkı bulunmamaktadır.
              ` : `
Cebime Gelsin Kullanıcı Sözleşmesi

1. Taraflar
Bu sözleşme, Cebime Gelsin platformu ile kullanıcı arasında akdedilmiştir.

2. Hizmetin Tanımı
Cebime Gelsin, alıcılar ile satıcıları (Emlak ofisleri, Galeriler) bir araya getiren bir pazar yeri platformudur. Platform, alım-satım işlemine taraf değildir.

3. Kullanıcı Yükümlülükleri
Kullanıcılar, sisteme girdikleri bilgilerin doğruluğundan sorumludur. Yanıltıcı bilgi veren hesaplar askıya alınabilir.

4. Fikri Mülkiyet
Sitedeki tüm logo, tasarım ve yazılımlar Cebime Gelsin'e aittir. İzinsiz kopyalanamaz.
              `}
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8 bg-white dark:bg-gray-900 transition-colors">
      <div className="max-w-4xl mx-auto">
        <button 
          onClick={onBack}
          className="flex items-center text-gray-500 hover:text-gray-900 dark:hover:text-white mb-8 font-bold transition-colors"
        >
          <ArrowLeft className="w-5 h-5 mr-2" /> Geri Dön
        </button>
        
        <div className="bg-white dark:bg-gray-900">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default StaticPages;